import { useCallback } from 'react';
import { usePieceDispatch, usePieceSelector } from '../../model/piece-context';
import { generateColorImage } from '../../media/utils/image-generation';

type UseBackgroundOptions = {
  designWidth: number;
  designHeight: number;
};

export type UseBackgroundReturn = {
  applyColor: (color: string) => Promise<void>;
};

/**
 * Single entry point for managing the background. Dispatches to the model; the
 * renderer reconciles the canvas object (see `applyBackground`).
 */
export const useBackground = ({ designWidth, designHeight }: UseBackgroundOptions): UseBackgroundReturn => {
  const dispatch = usePieceDispatch();
  const kind = usePieceSelector(m => m.background?.kind);

  const applyColor = useCallback(
    async (color: string) => {
      // An image background can't take a fill, so the colour has to become pixels.
      if (kind === 'image') {
        const src = await generateColorImage(color, designWidth, designHeight);
        dispatch({ type: 'setBackground', background: { kind: 'image', src } });
        return;
      }
      dispatch({ type: 'setBackground', background: { kind: 'color', color } });
    },
    [dispatch, kind, designWidth, designHeight]
  );

  return { applyColor };
};
